import React, { useState, useEffect, useRef } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDomain } from '../context/DomainContext';
import { motion, useAnimation, useInView } from 'framer-motion';
import confetti from 'canvas-confetti';
import { campaignService } from '../services/api.service';

// Animaciones
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
  
  body {
    margin: 0;
    padding: 0;
    font-family: 'Poppins', sans-serif;
    background: linear-gradient(135deg, #581C87 0%, #4C1D95 100%);
    color: white;
    overflow-x: hidden;
    line-height: 1.6;
  }

  .highlight {
    color: #ffd700;
    font-weight: 700;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 8px;
      background: rgba(255, 215, 0, 0.3);
      bottom: -2px;
      left: 0;
      z-index: -1;
      border-radius: 4px;
    }
  }

  .cta-button {
    background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
    color: #581C87;
    border: none;
    padding: 1rem 2rem;
    border-radius: 50px;
    font-weight: 700;
    font-size: 1.2rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
    text-decoration: none;
    display: inline-block;
    text-align: center;
    margin: 1rem 0;
    position: relative;
    overflow: hidden;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #ffb347 0%, #ffd700 100%);
      z-index: -1;
      transition: opacity 0.3s ease;
      opacity: 0;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
      
      &::before {
        opacity: 1;
      }
    }

    &:active {
      transform: translateY(0);
    }
  }

  .section-title {
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    text-align: center;
    background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: linear-gradient(90deg, #ffd700, #ffb347);
      border-radius: 2px;
    }
  }

  .section-subtitle {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #ffd700;
    text-align: center;
  }

  .benefit-card {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 2rem;
    margin: 1rem 0;
    border: 1px solid rgba(255, 215, 0, 0.2);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 179, 71, 0.1) 100%);
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      
      &::before {
        opacity: 1;
      }
    }

    h3 {
      color: #ffd700;
      font-size: 1.5rem;
      margin-bottom: 1rem;
      position: relative;
      display: inline-block;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 40px;
        height: 3px;
        background: #ffd700;
        border-radius: 1.5px;
      }
    }

    p {
      font-size: 1.1rem;
      line-height: 1.6;
    }
  }

  .testimonial {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    padding: 2rem;
    margin: 2rem 0;
    border-left: 4px solid #ffd700;
    font-style: italic;
    position: relative;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    }

    &::before {
      content: '"';
      font-size: 5rem;
      color: rgba(255, 215, 0, 0.2);
      position: absolute;
      top: -1rem;
      left: 1rem;
    }

    p {
      font-size: 1.2rem;
      line-height: 1.6;
      margin-bottom: 1rem;
    }

    .author {
      font-weight: 700;
      color: #ffd700;
      display: flex;
      align-items: center;
      
      &::before {
        content: '';
        display: inline-block;
        width: 30px;
        height: 3px;
        background: #ffd700;
        margin-right: 10px;
        border-radius: 1.5px;
      }
    }
  }

  .stats-container {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
    margin: 3rem 0;
  }

  .stat-item {
    text-align: center;
    margin: 1rem;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 15px;
    min-width: 200px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 179, 71, 0.1) 100%);
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-5px);
      background: rgba(255, 255, 255, 0.1);
      
      &::before {
        opacity: 1;
      }
    }

    .stat-number {
      font-size: 3rem;
      font-weight: 800;
      color: #ffd700;
      margin-bottom: 0.5rem;
      position: relative;
      display: inline-block;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 100%;
        height: 3px;
        background: linear-gradient(90deg, #ffd700, transparent);
        border-radius: 1.5px;
      }
    }

    .stat-label {
      font-size: 1.1rem;
      color: white;
    }
  }

  .pricing-card {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 2rem;
    margin: 2rem 0;
    border: 1px solid rgba(255, 215, 0, 0.2);
    text-align: center;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 179, 71, 0.1) 100%);
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      
      &::before {
        opacity: 1;
      }
    }

    .price {
      font-size: 3rem;
      font-weight: 800;
      color: #ffd700;
      margin: 1rem 0;
      position: relative;
      display: inline-block;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 100%;
        height: 3px;
        background: linear-gradient(90deg, #ffd700, transparent);
        border-radius: 1.5px;
      }
    }

    .price-details {
      font-size: 1.1rem;
      margin-bottom: 1.5rem;
    }

    ul {
      text-align: left;
      margin: 1.5rem 0;
      padding-left: 1.5rem;

      li {
        margin: 0.8rem 0;
        font-size: 1.1rem;
        position: relative;
        padding-left: 1.5rem;
        transition: all 0.3s ease;

        &:hover {
          transform: translateX(5px);
          color: #ffd700;
        }

        &::before {
          content: '✓';
          color: #ffd700;
          position: absolute;
          left: 0;
        }
      }
    }
  }

  .faq-item {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 15px;
    padding: 1.5rem;
    margin: 1rem 0;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 179, 71, 0.1) 100%);
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: translateX(5px);
      
      &::before {
        opacity: 1;
      }
    }

    .question {
      font-size: 1.2rem;
      font-weight: 600;
      color: #ffd700;
      margin-bottom: 0.5rem;
      position: relative;
      display: inline-block;
      
      &::after {
        content: '';
        position: absolute;
        bottom: -3px;
        left: 0;
        width: 30px;
        height: 2px;
        background: #ffd700;
        border-radius: 1px;
      }
    }

    .answer {
      font-size: 1.1rem;
      line-height: 1.6;
    }
  }

  @media (max-width: 768px) {
    .section-title {
      font-size: 2rem;
    }

    .section-subtitle {
      font-size: 1.3rem;
    }

    .benefit-card, .pricing-card {
      padding: 1.5rem;
    }

    .stat-item {
      min-width: 150px;
    }
  }
`;

// Componente para manejar las animaciones de sección
const AnimatedSection = ({ children, delay = 0 }) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: delay
      }
    }
  };

  return (
    <motion.div
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
    >
      {children}
    </motion.div>
  );
};

// Componente para elementos animados individuales
const AnimatedItem = ({ children, delay = 0 }) => {
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        delay
      }
    }
  };

  return (
    <motion.div variants={itemVariants}>
      {children}
    </motion.div>
  );
};

// Añadir el styled component para el logo
const LogoImage = styled.img`
  width: 150px;
  height: 150px;
  margin: 2rem auto;
  animation: ${float} 6s ease-in-out infinite;
  filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.3));
`;

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
  align-items: center;
  justify-items: center;
`;

const BusinessLogo = styled.img`
  width: 150px;
  height: 150px;
  object-fit: contain;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px rgba(255, 215, 0, 0.2);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #581C87 0%, #4C1D95 100%);
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  position: relative;
  transform: ${props => props.show ? 'scale(1)' : 'scale(0.9)'};
  opacity: ${props => props.show ? 1 : 0};
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #ffd700;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    color: #ffb347;
  }
`;

const ModalTitle = styled.h3`
  font-size: 2rem;
  color: #ffd700;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const PrizesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const PrizeItem = styled.li`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::before {
    content: '🎁';
    margin-right: 1rem;
    font-size: 1.2rem;
  }
`;

const InstagramNote = styled.div`
  font-size: 0.95rem;
  color: #ffd700;
  margin-top: 0.5rem;
  text-align: left;
  width: 100%;
  font-style: italic;
`;

const CardFootnote = styled.div`
  font-size: 0.95rem;
  color: #ffd700;
  margin-top: 0.7rem;
  text-align: left;
  width: 100%;
  font-style: italic;
`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { shortName, displayName, appName } = useDomain();
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [finishedCampaigns, setFinishedCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const loadFinishedCampaigns = async () => {
      try {
        const response = await campaignService.getFinishedCampaigns();
        if (response.success) {
          setFinishedCampaigns(response.campaigns);
        }
      } catch (error) {
        console.error('Error loading finished campaigns:', error);
      }
    };

    loadFinishedCampaigns();
  }, []);

  const handleCtaClick = () => {
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
    }, 3000);
    
    // Redirigir al formulario de contacto
    window.open('https://tally.so/r/nGb6pe', '_blank');
  };

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const shootConfetti = () => {
    const end = Date.now() + 3000;
    const colors = ['#ffd700', '#ffb347', '#ffed4a'];

    const shoot = () => {
      confetti({
        particleCount: 80,
        angle: 90,
        spread: 120,
        origin: { x: 0.5, y: 0.4 },
        colors: colors,
        shapes: ['circle', 'square'],
        gravity: 0.7,
        scalar: 1.5,
        drift: 0,
        ticks: 200,
        zIndex: 9999,
        disableForReducedMotion: true
      });
    };

    shoot();

    const interval = setInterval(() => {
      if (Date.now() > end) {
        return clearInterval(interval);
      }
      shoot();
    }, 250);

    return () => clearInterval(interval);
  };

  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedCampaign(null), 300);
  };

  return (
    <>
      <GlobalStyle />
      {showConfetti && shootConfetti()}
      
      <Modal show={showModal} onClick={closeModal}>
        <ModalContent show={showModal} onClick={e => e.stopPropagation()}>
          <CloseButton onClick={closeModal}>×</CloseButton>
          {selectedCampaign && (
            <>
              <ModalTitle>{selectedCampaign.name}</ModalTitle>
              <PrizesList>
                {selectedCampaign.prizes.map((prize, index) => (
                  <PrizeItem key={index}>
                    {prize.name}
                  </PrizeItem>
                ))}
              </PrizesList>
            </>
          )}
        </ModalContent>
      </Modal>

      <Container>
        <HeroSection>
          <HeroContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <HeroTitle>
                {appName}
              </HeroTitle>
              <HeroSubtitle>
                La promoción que <span className="highlight">convierte visitantes en clientes</span>
              </HeroSubtitle>
              <LogoImage src="/android-chrome-512x512.png" alt={`${shortName} Logo`} />
              <HeroDescription>
                Conecta directamente con más de <span className="highlight">25.000 pamploneses</span>. Tu negocio se promociona a través de nuestro boletín de WhatsApp, <span className="highlight">con una tasa de apertura del 80%</span>.
              </HeroDescription>
              <CtaButton onClick={handleCtaClick}>
                ¡Impulsa tu negocio ahora!
              </CtaButton>
            </motion.div>
          </HeroContent>
        </HeroSection>

        <Section>
          <AnimatedSection>
            <SectionTitle>¿Qué es {shortName}?</SectionTitle>
            <SectionDescription>
              Un juego interactivo y adictivo en el que los usuarios rascan una tarjeta virtual para descubrir premios, creando una experiencia memorable que hace que tu marca se destaque y se recuerde. La gamificación aumenta el engagement y genera conversaciones sobre tu negocio.
            </SectionDescription>
            <FeatureGrid>
              <AnimatedItem>
                <FeatureCard>
                  <FeatureIcon>🎯</FeatureIcon>
                  <FeatureTitle>Alcance Preciso</FeatureTitle>
                  <FeatureDescription>
                    Llega directamente a tu público objetivo: pamploneses interesados en ofertas y promociones locales.
                  </FeatureDescription>
                </FeatureCard>
              </AnimatedItem>

              <AnimatedItem>
                <FeatureCard>
                  <FeatureIcon>🎁</FeatureIcon>
                  <FeatureTitle>Premios Personalizados</FeatureTitle>
                  <FeatureDescription>
                    Define tus propios premios para dirigir a los usuarios a tu negocio.
                  </FeatureDescription>
                </FeatureCard>
              </AnimatedItem>
              <AnimatedItem>
                <FeatureCard>
                  <FeatureIcon>📊</FeatureIcon>
                  <FeatureTitle>Analíticas Detalladas</FeatureTitle>
                  <FeatureDescription>
                    Obtén informes completos sobre participación de tu campaña.
                  </FeatureDescription>
                </FeatureCard>
              </AnimatedItem>
            </FeatureGrid>
          </AnimatedSection>
        </Section>

        <Section>
          <AnimatedSection delay={0.2}>
            <SectionTitle>Beneficios Comprobados</SectionTitle>
            
            <BenefitCards>
              <AnimatedItem>
                <BenefitCard>
                  <BenefitTitle>Alcance Comprobado</BenefitTitle>
                  <BenefitDescription>
                    Accede a una base de datos de más de 25.000 suscriptores pamploneses interesados en ofertas locales.
                  </BenefitDescription>
                </BenefitCard>
              </AnimatedItem>
              <AnimatedItem>
                <BenefitCard>
                  <BenefitTitle>Engagement Real</BenefitTitle>
                  <BenefitDescription>
                    Con una tasa de apertura del 80%, superamos ampliamente la media del sector. Nuestro formato de WhatsApp garantiza que tu mensaje sea visto y recordado por tus potenciales clientes.
                  </BenefitDescription>
                </BenefitCard>
              </AnimatedItem>
              <AnimatedItem>
                <BenefitCard>
                  <BenefitTitle>Interacción Directa</BenefitTitle>
                  <BenefitDescription>
                    Más de 1.500 usuarios participan diariamente en nuestras promociones. Esto significa conversaciones reales sobre tu negocio y un aumento significativo de la notoriedad de tu marca.
                  </BenefitDescription>
                </BenefitCard>
              </AnimatedItem>
              
            </BenefitCards>
          </AnimatedSection>
        </Section>

        <ComparisonSection>
          <ComparisonTitle>¿Dónde invertir tu presupuesto?</ComparisonTitle>
          <ComparisonGrid>
            <ComparisonCard>
              <CardTitle><span className="icon">📸</span> Instagram Ads</CardTitle>
              <CardPrice>50€</CardPrice>
              <CardList>
                <li><span className="icon">👀</span> Alcance: <b>8.500</b> personas</li>
                <li><span className="icon">🔁</span> Impresiones: <b>15.400</b></li>
                <li><span className="icon">👤</span> Visitas al perfil: <b>278</b></li>
                <li><span className="icon">❤️</span> Me gusta: <b>25</b></li>
                <li><span className="icon">💬</span> Interacción limitada</li>
                <li><span className="icon">📉</span> Difícil de medir conversión real</li>
              </CardList>
              <InstagramNote>
                *Datos reales de una campaña de un negocio local de Pamplona
              </InstagramNote>
              <CardFootnote>
                Resultados de una campaña de 15 días
              </CardFootnote>
            </ComparisonCard>
            <ComparisonCard style={{ border: '2.5px solid #ffd700', boxShadow: '0 8px 32px rgba(255,215,0,0.10)' }}>
              <RecommendedBadge>Recomendado</RecommendedBadge>
              <CardTitle><span className="icon">🎯</span> {shortName} {displayName}</CardTitle>
              <CardPrice>50€</CardPrice>
              <CardList>
                <li><span className="icon">👥</span> Alcance real: <b>+25.000</b> pamploneses</li>
                <li><span className="icon">📲</span> Tasa de apertura: <b>80%</b></li>
                <li><span className="icon">🎲</span> Participación media: <b>1.500/día</b></li>
                <li><span className="icon">💬</span> Engagement directo y conversación</li>
                <li><span className="icon">📊</span> Analítica detallada y transparente</li>
                <li><span className="icon">🏆</span> Premios personalizados para tu público</li>
                <li><span className="icon">🚀</span> Promoción destacada en WhatsApp</li>
              </CardList>
              <CardFootnote>
                Resultados de un solo día
              </CardFootnote>
            </ComparisonCard>
          </ComparisonGrid>
          <ComparisonSummary>
            <span style={{ color: '#fff', fontWeight: 400 }}>
              Por el <b>mismo precio</b>, tu campaña con {displayName} llega a <b>casi 3 veces más personas</b>, con una tasa de apertura y participación mucho mayor, y una conexión directa con tu público local.<br /><br />
              <span style={{ color: '#ffd700', fontWeight: 700 }}>{displayName} es la opción inteligente para negocios que quieren resultados reales en Pamplona.</span>
            </span>
          </ComparisonSummary>
        </ComparisonSection>

        <Section>
          <AnimatedSection delay={0.4}>
            <SectionTitle>Inversión</SectionTitle>
            <SectionDescription>
              Una inversión única que multiplicará tus resultados y te conectará directamente con miles de potenciales clientes en Pamplona.
            </SectionDescription>
            <PricingCard>
              <Price>
                <OldPrice>100€</OldPrice>
                <NewPrice>50€</NewPrice>
                <PromoText>Solo hasta julio</PromoText>
              </Price>
              <PriceDetails>Inversión única para impulsar tu negocio</PriceDetails>
              
              <PricingFeatures>
                <AnimatedItem>
                  <PricingFeature>
                    <PricingFeatureIcon>🎨</PricingFeatureIcon>
                    <FeatureText>Campaña personalizada con tu marca y diseño exclusivo</FeatureText>
                  </PricingFeature>
                </AnimatedItem>
                <AnimatedItem>
                  <PricingFeature>
                    <PricingFeatureIcon>👥</PricingFeatureIcon>
                    <FeatureText>Acceso a más de 25.000 suscriptores pamploneses</FeatureText>
                  </PricingFeature>
                </AnimatedItem>
                <AnimatedItem>
                  <PricingFeature>
                    <PricingFeatureIcon>📱</PricingFeatureIcon>
                    <FeatureText>Promoción destacada en nuestro boletín de WhatsApp</FeatureText>
                  </PricingFeature>
                </AnimatedItem>
                <AnimatedItem>
                  <PricingFeature>
                    <PricingFeatureIcon>📊</PricingFeatureIcon>
                    <FeatureText>Panel de control con estadísticas detalladas</FeatureText>
                  </PricingFeature>
                </AnimatedItem>
                <AnimatedItem>
                  <PricingFeature>
                    <PricingFeatureIcon>🛠️</PricingFeatureIcon>
                    <FeatureText>Soporte técnico completo durante toda la campaña</FeatureText>
                  </PricingFeature>
                </AnimatedItem>
                <AnimatedItem>
                  <PricingFeature>
                    <PricingFeatureIcon>💎</PricingFeatureIcon>
                    <FeatureText>Sin costes adicionales ocultos ni sorpresas</FeatureText>
                  </PricingFeature>
                </AnimatedItem>
              </PricingFeatures>
              
              
              <PricingCtaButton onClick={handleCtaClick}>
                ¡Destaca tu marca ahora!
              </PricingCtaButton>
            </PricingCard>
          </AnimatedSection>
        </Section>

        <Section>
          <AnimatedSection delay={0.6}>
            <SectionTitle>Negocios que confían en nosotros</SectionTitle>
            <SectionDescription>
              Estos son algunos de los negocios que ya han impulsado sus ventas con {shortName}.
            </SectionDescription>
            <LogoGrid>
              {finishedCampaigns.map((campaign, index) => (
                campaign.logo && (
                  <AnimatedItem key={campaign._id} delay={index * 0.1}>
                    <BusinessLogo 
                      src={campaign.logo} 
                      alt={`Logo de ${campaign.name}`}
                      onClick={() => handleCampaignClick(campaign)}
                      style={{ cursor: 'pointer' }}
                    />
                  </AnimatedItem>
                )
              ))}
            </LogoGrid>
          </AnimatedSection>
        </Section>

        <CtaSection>
          <AnimatedSection delay={1}>
            <CtaTitle>¿Listo para impulsar tu negocio?</CtaTitle>
            <CtaDescription>
              Únete a los negocios que ya están destacando en Pamplona con {shortName}.
            </CtaDescription>
            <CtaButton onClick={handleCtaClick}>
              ¡Crea tu campaña ahora!
            </CtaButton>
          </AnimatedSection>
        </CtaSection>

        <Footer>
          <FooterContent>
            <FooterCopyright>
              © {new Date().getFullYear()} {displayName}. Todos los derechos reservados.
            </FooterCopyright>
          </FooterContent>
        </Footer>
      </Container>
    </>
  );
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 90vh;
  padding: 4rem 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem 0;
    min-height: auto;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 800px;
  text-align: center;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    padding-right: 0;
    margin-bottom: 2rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.2;
  background: linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #ffd700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    max-width: 100%;
  }
`;

const Section = styled.section`
  padding: 5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-align: center;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  border: 1px solid rgba(255, 215, 0, 0.2);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  animation: ${pulse} 3s ease-in-out infinite;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffd700;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
`;

const PricingFeatureIcon = styled.div`
  font-size: 2rem;
  margin-right: 1rem;
  color: #ffd700;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  margin: 3rem 0;
`;

const StatItem = styled.div`
  text-align: center;
  margin: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  min-width: 200px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #ffd700;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: white;
`;

const BenefitCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const BenefitCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(255, 215, 0, 0.2);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

const BenefitTitle = styled.h3`
  color: #ffd700;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const BenefitDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
`;

const PricingCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 3rem;
  margin: 2rem auto;
  border: 1px solid rgba(255, 215, 0, 0.2);
  text-align: center;
  max-width: 800px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 179, 71, 0.1) 100%);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
`;

const Price = styled.div`
  font-size: 4rem;
  font-weight: 800;
  color: #ffd700;
  margin: 1rem 0;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ffd700, #ffb347);
    border-radius: 2px;
  }
`;

const OldPrice = styled.span`
  color: #fff;
  opacity: 0.6;
  text-decoration: line-through;
  font-size: 2.2rem;
  margin-right: 1.2rem;
  vertical-align: middle;
`;

const NewPrice = styled.span`
  color: #ffd700;
  font-size: 3.2rem;
  font-weight: 900;
  vertical-align: middle;
`;

const PromoText = styled.div`
  font-size: 1rem;
  color: #ffd700;
  margin-top: 0.3rem;
  font-weight: 600;
`;

const PriceDetails = styled.div`
  font-size: 1.3rem;
  margin-bottom: 2rem;
  color: #ffd700;
  font-weight: 600;
`;

const PricingFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2rem 0;
`;

const PricingFeature = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(5px);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const FeatureText = styled.div`
  font-size: 1.1rem;
  line-height: 1.4;
  text-align: left;
`;

const PricingHighlight = styled.div`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 179, 71, 0.2) 100%);
  border-radius: 15px;
  padding: 1.5rem;
  margin: 2rem 0;
  border-left: 4px solid #ffd700;
  text-align: left;
  font-style: italic;
  position: relative;
  
  &::before {
    content: '"';
    font-size: 5rem;
    color: rgba(255, 215, 0, 0.2);
    position: absolute;
    top: -1rem;
    left: 1rem;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    margin-bottom: 0.5rem;
  }
  
  .highlight-author {
    font-weight: 700;
    color: #ffd700;
    text-align: right;
  }
`;

const CtaButton = styled.button`
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  color: #581C87;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const PricingCtaButton = styled(CtaButton)`
  margin-top: 2rem;
  padding: 1.2rem 2.5rem;
  font-size: 1.3rem;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  box-shadow: 0 8px 25px rgba(255, 215, 0, 0.4);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(255, 215, 0, 0.5);
  }
`;

const Testimonial = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2rem;
  margin: 2rem 0;
  border-left: 4px solid #ffd700;
  font-style: italic;
  position: relative;
  
  &::before {
    content: '"';
    font-size: 5rem;
    color: rgba(255, 215, 0, 0.2);
    position: absolute;
    top: -1rem;
    left: 1rem;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  
  .author {
    font-weight: 700;
    color: #ffd700;
  }
`;

const FaqItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  margin: 1rem 0;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &.active {
    background: rgba(255, 255, 255, 0.1);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  .question {
    font-size: 1.2rem;
    font-weight: 600;
    color: #ffd700;
    margin-bottom: 0.5rem;
  }
  
  .answer {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const CtaSection = styled.section`
  text-align: center;
  padding: 5rem 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(255, 215, 0, 0.1), transparent 70%);
    z-index: -1;
  }
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CtaDescription = styled.p`
  font-size: 1.2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Footer = styled.footer`
  padding: 3rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FooterLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #ffd700;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const FooterLink = styled.a`
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ffd700;
  }
`;

const FooterCopyright = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

// Tarjetas comparativas
const ComparisonSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5rem 0 2rem 0;
  background: linear-gradient(135deg, #4C1D95 0%, #581C87 100%);
`;

const ComparisonTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 800;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  width: 100%;
  max-width: 900px;
  margin-bottom: 2rem;
`;

const ComparisonCard = styled.div`
  background: rgba(255,255,255,0.08);
  border-radius: 20px;
  padding: 2.5rem 2rem 2rem 2rem;
  border: 2px solid rgba(255,215,0,0.15);
  box-shadow: 0 8px 32px rgba(0,0,0,0.10);
  position: relative;
  text-align: center;
  transition: all 0.3s ease;
  min-height: 370px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CardTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
  color: #ffd700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardPrice = styled.div`
  font-size: 2.2rem;
  font-weight: 800;
  color: #ffd700;
  margin-bottom: 1.2rem;
`;

const CardList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  width: 100%;
  text-align: left;

  li {
    font-size: 1.1rem;
    margin-bottom: 0.7rem;
    display: flex;
    align-items: center;
    gap: 0.7rem;
    color: #fff;
    
    .icon {
      font-size: 1.2rem;
      color: #ffd700;
    }
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -18px;
  right: -18px;
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  color: #581C87;
  font-weight: 800;
  font-size: 1rem;
  padding: 0.5rem 1.2rem;
  border-radius: 30px;
  box-shadow: 0 2px 8px rgba(255,215,0,0.15);
  z-index: 2;
  letter-spacing: 1px;
`;

const ComparisonSummary = styled.div`
  margin-top: 1.5rem;
  font-size: 1.2rem;
  color: #ffd700;
  text-align: center;
  max-width: 800px;
  font-weight: 600;
`;

export default LandingPage; 